import { zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import { Modal } from '../../../../components/Modal';
import ScheduleView from './ScheduleView';
import { useEffect, useRef, useState } from 'react';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { Agendamento, StatusHorario } from '../../Enum';
import { CancelModal } from './CancelHorario';
import { useAgendaClienteContext } from '../Context';
import { ConfirmHorario } from './ConfirmHorario';
import { AgendaClienteService } from '../Service';
import {
    canRequestCancellation,
    canRequestConfirmation,
    getRelativeDayLabel,
    getScheduleStatusLabel,
    getScheduleTone,
    getServiceLabel,
} from './helpers';
import { Bell, CalendarX, ChevronRight, MessageCircle, Navigation, QrCode, Repeat } from 'lucide-react';

const horariosService = new AgendaClienteService();

type CardVariant = 'hero' | 'row';

export const CardAgendamento: React.FC<{ agendamento: Agendamento; variant?: CardVariant }> = ({
    agendamento,
    variant = 'row',
}) => {
    const { loadAgendamentos } = useAgendaClienteContext();
    const [isNew, setIsNew] = useState(false);
    const [open, setOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const rowRef = useRef<HTMLDivElement>(null);
    const { action, id } = useParamsContext();

    // Agendamento recem-criado (action=new): destaque suave e rolagem ate ele.
    useEffect(() => {
        if (action === 'new' && agendamento._id === id) {
            setIsNew(true);
            rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [action, id, agendamento._id]);

    const { startAt, endAt, estab, attendant, status } = agendamento;
    const start = zonedDate(startAt, agendamento.timezone);
    const end = zonedDate(endAt, agendamento.timezone);
    const tone = getScheduleTone(status);
    const statusLabel = getScheduleStatusLabel(agendamento);
    const serviceLabel = getServiceLabel(agendamento);
    const isCancelled = status === StatusHorario.CANCELADO;
    const needsConfirmation = canRequestConfirmation(agendamento);
    const allowsCancellation = canRequestCancellation(agendamento);
    const pixPending = agendamento.payment?.status === 'PENDING';
    const estabWhatsapp = estab?.isPhoneNumberWhatsapp ? estab?.phoneNumber?.replace(/\D/g, '') : '';
    const mapsUrl = estab?.address?.addressUrl;

    const handleCancel = async () => {
        try {
            await horariosService.cancel(agendamento._id);
            loadAgendamentos();
        } catch (error) {
            console.error('Error cancelling agendamento:', error);
        } finally {
            setIsCancelModalOpen(false);
        }
    };

    const handleConfirm = async () => {
        try {
            await horariosService.confirm(agendamento._id);
            loadAgendamentos();
        } catch (error) {
            console.error('Error confirm agendamento:', error);
        } finally {
            setIsConfirmModalOpen(false);
        }
    };

    const tags = (
        <div className="sf-ma-row-tags">
            <span className="sf-ma-tag" data-tone={tone}>{statusLabel}</span>
            {pixPending && <span className="sf-ma-tag" data-tone="orange">Pix pendente</span>}
            {agendamento.payment?.status === 'PAID' && <span className="sf-ma-tag" data-tone="green">Pago</span>}
            {agendamento.recorrenciaId && (
                <span className="sf-ma-tag no-dot" data-tone="gray" title="Gerado por recorrência">
                    <Repeat className="text-[9px]" aria-hidden="true" /> Recorrente
                </span>
            )}
        </div>
    );

    const modals = (
        <>
            {open && (
                <Modal onClose={() => setOpen(false)} title="Agendamento">
                    <ScheduleView
                        schedule={agendamento}
                        onConfirm={needsConfirmation ? () => setIsConfirmModalOpen(true) : undefined}
                        onCancel={allowsCancellation ? () => setIsCancelModalOpen(true) : undefined}
                    />
                </Modal>
            )}

            {isCancelModalOpen && (
                <Modal
                    onClose={() => setIsCancelModalOpen(false)}
                    title="Cancelar agendamento?"
                    subTitle={`${serviceLabel} · ${start.format('D [de] MMMM [às] HH:mm')}`}
                >
                    <CancelModal onConfirm={handleCancel} onCancel={() => setIsCancelModalOpen(false)} />
                </Modal>
            )}

            {isConfirmModalOpen && (
                <Modal
                    onClose={() => setIsConfirmModalOpen(false)}
                    title="Confirmar presença?"
                    subTitle={`${serviceLabel} · ${start.format('D [de] MMMM [às] HH:mm')}`}
                >
                    <ConfirmHorario onConfirm={handleConfirm} onCancel={() => setIsConfirmModalOpen(false)} />
                </Modal>
            )}
        </>
    );

    if (variant === 'hero') {
        return (
            <div ref={rowRef} className="sf-ma-hero">
                <button type="button" className="sf-ma-hero-main" onClick={() => setOpen(true)}>
                    <div className="sf-ma-hero-eyebrow">
                        <span>{getRelativeDayLabel(startAt, undefined, agendamento.timezone)}</span>
                        <ChevronRight className="text-[11px] opacity-50" aria-hidden="true" />
                    </div>
                    <div className="sf-ma-hero-time">
                        {start.format('HH:mm')} <span>–</span> {end.format('HH:mm')}
                    </div>
                    <div className="sf-ma-hero-date">{start.format('dddd, D [de] MMMM')}</div>
                    <div className="sf-ma-hero-service">{serviceLabel}</div>
                    <div className="sf-ma-hero-meta">
                        {[attendant?.name, estab?.name].filter(Boolean).join(' · ')}
                    </div>
                    {tags}
                </button>

                {needsConfirmation && (
                    <div className="sf-ma-callout" data-tone="blue">
                        <Bell aria-hidden="true" />
                        <span>O estabelecimento pediu para você confirmar presença.</span>
                        <button type="button" className="sf-ma-pill" onClick={() => setIsConfirmModalOpen(true)}>
                            Confirmar
                        </button>
                    </div>
                )}

                {pixPending && (
                    <div className="sf-ma-callout" data-tone="orange">
                        <QrCode aria-hidden="true" />
                        <span>Pague o Pix para garantir seu horário.</span>
                        <button type="button" className="sf-ma-pill is-tinted" onClick={() => setOpen(true)}>
                            Pagar
                        </button>
                    </div>
                )}

                {(estabWhatsapp || mapsUrl || allowsCancellation) && (
                    <div className="sf-ma-actions">
                        {estabWhatsapp && (
                            <a href={`https://wa.me/${estabWhatsapp}`} target="_blank" rel="noopener noreferrer"
                                className="sf-ma-action is-whatsapp">
                                <MessageCircle aria-hidden="true" />
                                <span>WhatsApp</span>
                            </a>
                        )}
                        {mapsUrl && (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="sf-ma-action">
                                <Navigation aria-hidden="true" />
                                <span>Como chegar</span>
                            </a>
                        )}
                        {allowsCancellation && (
                            <button type="button" className="sf-ma-action" onClick={() => setIsCancelModalOpen(true)}>
                                <CalendarX aria-hidden="true" />
                                <span>Cancelar</span>
                            </button>
                        )}
                    </div>
                )}

                {modals}
            </div>
        );
    }

    return (
        <div ref={rowRef} className="sf-ma-row-wrap">
            <button
                type="button"
                className={`sf-ma-row ${isNew ? 'is-new' : ''} ${isCancelled ? 'is-cancelled' : ''}`}
                onClick={() => setOpen(true)}
                aria-label={`${serviceLabel}, ${start.format('dddd, D [de] MMMM [às] HH:mm')}, ${statusLabel}`}
            >
                <span className="sf-ma-date" aria-hidden="true">
                    <span className="sf-ma-date-weekday">{start.format('ddd').replace('.', '')}</span>
                    <span className="sf-ma-date-day">{start.format('D')}</span>
                </span>
                <span className="sf-ma-row-body">
                    <span className="sf-ma-row-title block">{serviceLabel}</span>
                    <span className="sf-ma-row-sub block">
                        <span className="sf-ma-row-time">{start.format('HH:mm')}–{end.format('HH:mm')}</span>
                        {' · '}
                        {estab?.name}
                    </span>
                    {tags}
                </span>
                <ChevronRight className="sf-ma-chevron" aria-hidden="true" />
            </button>

            {needsConfirmation && (
                <div className="sf-ma-callout" data-tone="blue">
                    <Bell aria-hidden="true" />
                    <span>Confirme sua presença</span>
                    <button type="button" className="sf-ma-pill" onClick={() => setIsConfirmModalOpen(true)}>
                        Confirmar
                    </button>
                </div>
            )}

            {modals}
        </div>
    );
};
